import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bookmark, Eye, Flame, ListOrdered, Menu, Moon, Search, Settings, SlidersHorizontal, Sun, Swords, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Logo, TmdbAttribution } from "@/components/Brand";
import { GuestNotice, SaveProgressButton } from "@/components/GuestNotice";
import { CloudUpload } from "lucide-react";

const LINKS = [
  { to: "/", label: "Discover", icon: Flame, id: "discover" },
  { to: "/search", label: "Search movies", icon: Search, id: "search" },
  { to: "/watchlist", label: "Watchlist", icon: Bookmark, id: "watchlist" },
  { to: "/seen", label: "Seen", icon: Eye, id: "seen" },
  { to: "/rank", label: "My ranking", icon: ListOrdered, id: "rank" },
  { to: "/faceoff", label: "Face-off", icon: Swords, id: "faceoff" },
  { to: "/genres", label: "Genre preferences", icon: SlidersHorizontal, id: "genres" },
  { to: "/settings", label: "Account settings", icon: Settings, id: "settings" },
];

export const ThemeToggle = ({ className = "" }) => {
  const { theme, toggle } = useTheme();
  return (
    <Button variant="ghost" size="icon" onClick={toggle} className={`rounded-full ${className}`} data-testid="theme-toggle-button" aria-label="Toggle theme">
      {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
};

function MenuLinks({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 mt-6">
      {LINKS.map(({ to, label, icon: Icon, id, badge }) => (
        <NavLink key={to} to={to} end onClick={onNavigate} data-testid={`menu-link-${id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${isActive ? "bg-primary/10 text-primary" : "hover:bg-accent"}`
          }>
          <Icon className="h-5 w-5" /> {label}
          {badge && <span className="ml-auto text-[10px] uppercase tracking-widest bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{badge}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const guest = !!user?.is_guest;
  const navigate = useNavigate();
  const signOut = async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="relative z-10 min-h-screen">
      <header data-testid="app-header" className="fixed top-0 inset-x-0 z-50 h-16 px-4 sm:px-6 flex items-center justify-between backdrop-blur-xl bg-background/75 border-b">
        <NavLink to="/" data-testid="header-home-link"><Logo className="text-xl" /></NavLink>
        <div className="flex items-center gap-1">
          {guest && <SaveProgressButton className="mr-1" />}
          <Button asChild variant="ghost" size="icon" className="rounded-full" aria-label="Search movies">
            <NavLink to="/search" data-testid="header-search-button"><Search className="h-5 w-5" /></NavLink>
          </Button>
          <ThemeToggle className={guest ? "hidden sm:inline-flex" : ""} />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="hamburger-menu-button" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] flex flex-col" data-testid="menu-sheet">
              <SheetHeader className="text-left">
                <SheetTitle><Logo className="text-xl" /></SheetTitle>
                <p className="text-sm text-muted-foreground truncate" data-testid="menu-user-email">
                  {guest ? "Guest · progress kept in this browser only" : user?.email}
                </p>
              </SheetHeader>
              <MenuLinks onNavigate={() => setOpen(false)} />
              {guest ? (
                <NavLink to="/login" onClick={() => setOpen(false)} data-testid="menu-save-progress"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl font-bold mt-1 bg-amber-400 text-black hover:bg-amber-300">
                  <CloudUpload className="h-5 w-5" /> Save progress / Log in
                </NavLink>
              ) : (
                <button onClick={signOut} data-testid="logout-button"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-muted-foreground hover:bg-accent mt-1">
                  <LogOut className="h-5 w-5" /> Sign out
                </button>
              )}
              <div className="mt-auto pt-6"><TmdbAttribution compact /></div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      <main className="pt-20 pb-10 px-4 sm:px-6 lg:px-8">
        {guest && <GuestNotice />}
        <Outlet />
      </main>
    </div>
  );
}
